import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

// ── A4 page constants ─────────────────────────────────────────────────────────
const PAGE_W_MM    = 210;
const PAGE_H_MM    = 297;
const HEADER_MM    = 55;                               // letterhead header zone
const FOOTER_MM    = 40;                               // letterhead footer zone
const SIDE_MM      = 18;
const CONTENT_H_MM = PAGE_H_MM - HEADER_MM - FOOTER_MM; // 202 mm usable per page

const MM           = (n: number) => `${n}mm`;
const MM_TO_PX     = 3.7795275591;
const CONTENT_H_PX = CONTENT_H_MM * MM_TO_PX;         // ≈ 763.5 px

interface Quotation {
  id: number;
  QuotationNo: string;
  Company_id: number;
  Customer_id: number;
  Date: string;
  Total_Amount: number;
  status?: string;
  version?: string;
  letterhead_url?: string;
  pdf_url?: string;
  mediaUrl?: string;
  [key: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────────
// buildContentHtml — quotation body as an HTML string.
// Shared between screen preview (via dangerouslySetInnerHTML) and
// the iframe print HTML, so both always render identically.
// ─────────────────────────────────────────────────────────────────────────────
const buildContentHtml = (q: any, comp: any, cust: any): string => {
  const companyName    = comp?.company_name || 'Unknown Company';
  const companyAddress = comp ? [comp.address, comp.city, comp.state].filter(Boolean).join(', ') : '';
  const companyGST     = comp?.gst_number || '';
  const companyPAN     = comp?.pancard || '';
  const companyEmail   = comp?.email || '';
  const customerName   = cust?.company_name || 'Unknown Customer';
  const customerAddr   = cust ? [cust.address, cust.city, cust.state].filter(Boolean).join(', ') : '';
  const customerGST    = cust?.gst_number || '';
  const quotDate       = q.Date ? new Date(q.Date).toLocaleDateString('en-GB') : '';
  const totalAmt       = Number(q.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const S = (css: string) => `style="${css}"`;

  return `
<div ${S('font-family:Arial,sans-serif;font-size:11pt;color:#1a1a1a;line-height:1.5;')}>

  <div ${S('border-bottom:2px solid #1e3a8a;padding-bottom:12px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:flex-start;')}>
    <div>
      <div ${S('font-size:14pt;font-weight:700;color:#1e3a8a;text-transform:uppercase;')}>${companyName}</div>
      ${companyAddress ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>${companyAddress}</div>` : ''}
      ${companyGST     ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>GSTIN: ${companyGST}${companyPAN ? ' | PAN: ' + companyPAN : ''}</div>` : ''}
      ${companyEmail   ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>Email: ${companyEmail}</div>` : ''}
    </div>
    <div ${S('text-align:right;')}>
      <div ${S('font-size:18pt;font-weight:900;color:#1e3a8a;')}>QUOTATION</div>
      <div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}><strong>No:</strong> ${q.QuotationNo}</div>
      <div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}><strong>Date:</strong> ${quotDate}</div>
    </div>
  </div>

  <div ${S('display:flex;gap:16px;margin-bottom:14px;')}>
    <div ${S('flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:5px;padding:9px 11px;')}>
      <div ${S('font-size:7pt;font-weight:700;text-transform:uppercase;color:#64748b;letter-spacing:0.5px;margin-bottom:3px;')}>From (Company)</div>
      <div ${S('font-size:10pt;font-weight:700;color:#0f172a;')}>${companyName}</div>
      ${companyAddress ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>${companyAddress}</div>` : ''}
      ${companyGST     ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>GSTIN: ${companyGST}</div>` : ''}
    </div>
    <div ${S('flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:5px;padding:9px 11px;')}>
      <div ${S('font-size:7pt;font-weight:700;text-transform:uppercase;color:#64748b;letter-spacing:0.5px;margin-bottom:3px;')}>To (Customer)</div>
      <div ${S('font-size:10pt;font-weight:700;color:#0f172a;')}>${customerName}</div>
      ${customerAddr ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>${customerAddr}</div>` : ''}
      ${customerGST  ? `<div ${S('font-size:8.5pt;color:#475569;margin-top:2px;')}>GSTIN: ${customerGST}</div>` : ''}
    </div>
  </div>

  <div ${S('background:#1e3a8a;color:white;border-radius:5px;padding:9px 13px;text-align:center;margin-bottom:14px;')}>
    <div ${S('font-size:7.5pt;text-transform:uppercase;letter-spacing:0.5px;opacity:0.8;')}>Total Quotation Amount</div>
    <div ${S('font-size:15pt;font-weight:900;margin-top:2px;')}>&#8377; ${totalAmt}</div>
  </div>

  ${q.Particulars ? `
  <div ${S('margin-bottom:12px;')}>
    <div ${S('font-size:8pt;font-weight:700;text-transform:uppercase;color:#1e3a8a;letter-spacing:0.5px;border-bottom:1px solid #bfdbfe;padding-bottom:3px;margin-bottom:6px;')}>Brief Particulars</div>
    <div ${S('font-size:10pt;color:#334155;')}>${q.Particulars}</div>
  </div>` : ''}

  ${q.scope_of_work ? `
  <div ${S('margin-bottom:12px;')}>
    <div ${S('font-size:8pt;font-weight:700;text-transform:uppercase;color:#1e3a8a;letter-spacing:0.5px;border-bottom:1px solid #bfdbfe;padding-bottom:3px;margin-bottom:8px;')}>Scope of Work &amp; Terms</div>
    <div ${S('font-size:10pt;line-height:1.6;color:#1e293b;')}>${q.scope_of_work}</div>
  </div>` : ''}

  ${q.remarks ? `
  <div ${S('margin-top:16px;border-top:1px dashed #cbd5e1;padding-top:9px;')}>
    <div ${S('font-size:8pt;font-weight:700;text-transform:uppercase;color:#64748b;letter-spacing:0.5px;border-bottom:1px solid #e2e8f0;padding-bottom:3px;margin-bottom:6px;')}>Remarks / Notes</div>
    <div ${S('font-size:9pt;color:#475569;')}>${q.remarks}</div>
  </div>` : ''}

</div>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// QuotationBody — React wrapper around the shared HTML content
// ─────────────────────────────────────────────────────────────────────────────
interface QuotationBodyProps { q: any; comp: any; cust: any; }
const QuotationBody: React.FC<QuotationBodyProps> = ({ q, comp, cust }) => (
  <div dangerouslySetInnerHTML={{ __html: buildContentHtml(q, comp, cust) }} />
);

// ─────────────────────────────────────────────────────────────────────────────
// PagedPreview — multi-page A4 screen preview.
// Uses <img> for letterhead (not CSS background) so screen = print exactly.
// ─────────────────────────────────────────────────────────────────────────────
interface PagedPreviewProps {
  q: any; comp: any; cust: any; letterheadSrc: string;
  onNumPages?: (n: number) => void;
}

const PagedPreview: React.FC<PagedPreviewProps> = ({ q, comp, cust, letterheadSrc, onNumPages }) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(1);

  useLayoutEffect(() => {
    if (measureRef.current) {
      const h     = measureRef.current.scrollHeight;
      const pages = Math.max(1, Math.ceil(h / CONTENT_H_PX));
      setNumPages(pages);
      onNumPages?.(pages);
    }
  }, [q, onNumPages]);

  const hasLh  = Boolean(letterheadSrc);
  const padTop = hasLh ? MM(HEADER_MM) : MM(20);
  const padBot = hasLh ? MM(FOOTER_MM) : MM(20);

  return (
    <>
      {/* Hidden measurement div — measures content height to determine page count */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: 'fixed', top: -99999, left: -99999,
          width: `calc(${MM(PAGE_W_MM)} - ${MM(SIDE_MM * 2)})`,
          visibility: 'hidden', pointerEvents: 'none', zIndex: -1,
        }}
      >
        <QuotationBody q={q} comp={comp} cust={cust} />
      </div>

      {/* One A4 page div per page */}
      {Array.from({ length: numPages }, (_, i) => (
        <div
          key={i}
          style={{
            width: MM(PAGE_W_MM),
            height: MM(PAGE_H_MM),
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
            background: 'white',
          }}
        >
          {/*
           * ── LETTERHEAD AS <img> ──────────────────────────────────────────
           * We use an actual <img> element (not CSS background) because:
           *   • <img> elements ALWAYS print regardless of browser settings
           *   • CSS background-image requires "Background graphics" to be
           *     enabled in the print dialog AND can still silently fail
           * This guarantees the letterhead appears in every printed page.
           * ─────────────────────────────────────────────────────────────────
           */}
          {hasLh && (
            <img
              src={letterheadSrc}
              alt="company letterhead"
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'fill',
                display: 'block',
                zIndex: 0,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Page number badge */}
          <div style={{ position: 'absolute', bottom: 8, right: 12, fontSize: '7pt', color: '#94a3b8', zIndex: 10, pointerEvents: 'none', fontFamily: 'Arial, sans-serif' }}>
            Page {i + 1} / {numPages}
          </div>

          {/* Content viewport: clipped between letterhead header and footer zones */}
          <div style={{ position: 'absolute', top: padTop, bottom: padBot, left: MM(SIDE_MM), right: MM(SIDE_MM), overflow: 'hidden', zIndex: 1 }}>
            {/* Shift content up to show the correct slice for this page */}
            <div style={{ position: 'absolute', top: -(i * CONTENT_H_PX), left: 0, right: 0 }}>
              <QuotationBody q={q} comp={comp} cust={cust} />
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// buildPrintHtml — generates the iframe print document.
// Uses the SAME multi-page-div structure as PagedPreview and the SAME
// buildContentHtml function — guaranteed identical output to screen.
// Letterhead uses <img> not CSS background → always prints.
// ─────────────────────────────────────────────────────────────────────────────
const buildPrintHtml = (q: any, comp: any, cust: any, lh: string, nPages: number): string => {
  const hasLh   = Boolean(lh);
  const padTop  = hasLh ? HEADER_MM : 20;
  const padBot  = hasLh ? FOOTER_MM : 20;
  const content = buildContentHtml(q, comp, cust);

  const pageHtml = Array.from({ length: nPages }, (_, i) => {
    const offsetMm = -(i * CONTENT_H_MM);
    const isLast   = i === nPages - 1;
    return `
<div style="
  width:210mm;
  height:297mm;
  position:relative;
  overflow:hidden;
  background:white;
  ${isLast ? '' : 'page-break-after:always;break-after:page;'}
">
  ${hasLh ? `
  <!-- Letterhead: <img> always prints, unlike CSS background-image -->
  <img
    src="${lh}"
    alt="company letterhead"
    style="position:absolute;top:0;left:0;width:210mm;height:297mm;object-fit:fill;display:block;z-index:0;"
  />` : ''}

  <!-- Page number -->
  <div style="position:absolute;bottom:8px;right:12px;font-size:7pt;color:#94a3b8;font-family:Arial,sans-serif;z-index:10;">
    Page ${i + 1} / ${nPages}
  </div>

  <!-- Content viewport: respects letterhead header/footer safe zones -->
  <div style="position:absolute;top:${padTop}mm;bottom:${padBot}mm;left:${SIDE_MM}mm;right:${SIDE_MM}mm;overflow:hidden;z-index:1;">
    <!-- Shift content to show the correct page slice -->
    <div style="position:absolute;top:${offsetMm}mm;left:0;right:0;">
      ${content}
    </div>
  </div>
</div>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Quotation-${q.QuotationNo}</title>
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/*
 * margin:0 because each page div is exactly 297mm × 210mm.
 * All spacing is self-contained within the page divs.
 * In Chrome print dialog: set Margins → None, uncheck Headers and footers.
 */
@page { size: A4 portrait; margin: 0; }

html, body {
  margin: 0; padding: 0;
  font-family: Arial, sans-serif;
  background: #888;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

@media print {
  html, body { background: white; }
}

/* Rich-text scope content styles */
strong { font-weight: bold; }
em     { font-style: italic; }
h1, h2, h3 { color: #1e3a8a; }
h1 { font-size: 12pt; margin: 9px 0 4px; }
h2 { font-size: 11pt; margin: 9px 0 4px; }
h3 { font-size: 10.5pt; margin: 9px 0 4px; }
p  { margin-bottom: 5px; }
ul, ol { padding-left: 16px; margin-bottom: 5px; }
li { margin-bottom: 2px; }
table { width: 100%; border-collapse: collapse; margin-bottom: 9px; font-size: 9pt; }
table th, table td { border: 1px solid #e2e8f0; padding: 4px 7px; }
table th { background: #eff6ff; font-weight: 700; color: #1e3a8a; }
blockquote { border-left: 3px solid #bfdbfe; padding-left: 10px; color: #475569; margin: 6px 0; }
img { max-width: 100% !important; height: auto !important; display: block; margin: 6px auto; }
</style>
</head>
<body>
${pageHtml}
</body>
</html>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const QuotationPage: React.FC = () => {
  const [quotations,       setQuotations]       = useState<Quotation[]>([]);
  const [companies,        setCompanies]        = useState<any[]>([]);
  const [customers,        setCustomers]        = useState<any[]>([]);
  const [loading,          setLoading]          = useState(false);
  const [shareModalOpen,   setShareModalOpen]   = useState(false);
  const [shareChannel,     setShareChannel]     = useState<'email' | 'sms' | 'whatsapp'>('whatsapp');
  const [shareRecipient,   setShareRecipient]   = useState('');
  const [shareSubject,     setShareSubject]     = useState('');
  const [shareMessageText, setShareMessageText] = useState('');
  const [shareQuotation,   setShareQuotation]   = useState<Quotation | null>(null);
  const [shareSending,     setShareSending]     = useState(false);
  const [previewQuotation, setPreviewQuotation] = useState<any | null>(null);
  const [previewNumPages,  setPreviewNumPages]  = useState(1);

  const navigate       = useNavigate();
  const printIframeRef = useRef<HTMLIFrameElement>(null);

  const getCompany  = (id: any) => companies.find(c => String(c.id) === String(id));
  const getCustomer = (id: any) => customers.find(c => String(c.id) === String(id));

  const resolveLetterhead = (comp: any, quotation?: any): string => {
    const raw = quotation?.letterhead_url || comp?.letterhead || '';
    if (!raw) return '';
    if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
    return `${API_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
  };

  // Helper function to decode JWT token
  const decodeToken = (token: any) => {
    if (!token) return {};
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const storedRole = localStorage.getItem("role");
      let decoded: any = null;
      if (token) {
        decoded = decodeToken(token);
      }
      let compId: string | number | null = null;
      if (decoded) {
        compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      }

      const selectedCompanyHeader = localStorage.getItem("selected_company_id");
      const activeCompIdParam = (storedRole !== "super-admin" && compId)
        ? String(compId)
        : (selectedCompanyHeader && selectedCompanyHeader !== "all" ? selectedCompanyHeader : "");

      const [qRes, compRes, custRes] = await Promise.all([
        axios.get(`${API_URL}/auth/quotations`, { params: { selected_company_id: activeCompIdParam } }),
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/customers`),
      ]);

      const allQuots = qRes.data || [];
      const allComps = compRes.data || [];

      // Filter to only include quotation type (default is quotation)
      const allQuotationsOnly = allQuots.filter((q: any) => q.type !== 'contract');

      console.log('[DEBUG Quotation.tsx] storedRole:', storedRole);
      console.log('[DEBUG Quotation.tsx] compId (from token):', compId);
      console.log('[DEBUG Quotation.tsx] selectedCompanyHeader:', selectedCompanyHeader);
      console.log('[DEBUG Quotation.tsx] activeCompIdParam sent to API:', activeCompIdParam);
      console.log('[DEBUG Quotation.tsx] allQuotationsOnly count:', allQuotationsOnly.length);

      let activeCompId: string | number | null = null;
      if (storedRole !== "super-admin" && compId) {
        activeCompId = String(compId);
      } else if (selectedCompanyHeader && selectedCompanyHeader !== "all") {
        activeCompId = selectedCompanyHeader;
      }
      console.log('[DEBUG Quotation.tsx] activeCompId for filtering:', activeCompId);

      if (activeCompId) {
        const filtered = allQuotationsOnly.filter((q: any) => String(q.Company_id) === String(activeCompId));
        setQuotations(filtered);
        setCompanies(allComps.filter((c: any) => String(c.id) === String(activeCompId)));
      } else {
        setQuotations(allQuotationsOnly);
        setCompanies(allComps);
      }
      setCustomers(custRes.data  || []);
    } catch (err) { console.error('Error fetching quotations:', err); }
    finally { setLoading(false); }
  };

  const handleOpenPreview = async (q: Quotation) => {
    setPreviewNumPages(1);
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/auth/quotation/${q.id}`);
      setPreviewQuotation(res.data);
    } catch { alert('Failed to load quotation detail.'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    try { await axios.delete(`${API_URL}/auth/quotation/${id}`); fetchData(); }
    catch { alert('Failed to delete quotation'); }
  };

  const triggerShare = (q: Quotation, channel: 'email' | 'sms' | 'whatsapp') => {
    const cust     = getCustomer(q.Customer_id);
    const comp     = getCompany(q.Company_id);
    const custName = cust?.company_name || 'Customer';
    const compName = comp?.company_name || 'Our Company';
    const dateStr  = new Date(q.Date).toLocaleDateString('en-GB');
    const compPhone = comp ? (comp.phone || comp.number || comp.mobile || '') : '';
    const custPhone = cust ? (cust.whatsapp_number ? String(cust.whatsapp_number) : (cust.number ? String(cust.number) : (cust.phone ? String(cust.phone) : ''))) : '';
    const recipient = channel === 'email'
      ? (cust?.email || comp?.email || '')
      : (channel === 'whatsapp' ? (compPhone || custPhone) : (custPhone || compPhone));
    const messages: Record<string, string> = {
      email:    `Hello ${custName},\n\nPlease find the quotation details below:\nQuotation No: ${q.QuotationNo}\nDate: ${dateStr}\nTotal Amount: ₹ ${q.Total_Amount}\n\nRegards,\n${compName}`,
      sms:      `Dear ${custName}, quotation ${q.QuotationNo} of ₹ ${q.Total_Amount} is ready. - ${compName}`,
      whatsapp: `Hello *${custName}*,\n\nHere is your quotation detail:\n*Quotation No:* ${q.QuotationNo}\n*Date:* ${dateStr}\n*Total Amount:* ₹ ${q.Total_Amount}\n\nRegards,\n*${compName}*`,
    };
    setShareQuotation(q);
    setShareChannel(channel);
    setShareRecipient(recipient);
    setShareSubject(`Quotation: ${q.QuotationNo}`);
    setShareMessageText(messages[channel]);
    setShareModalOpen(true);
  };

  const handleExecuteShare = async () => {
    if (!shareRecipient.trim() || !shareMessageText.trim()) { alert('Recipient and message text are required.'); return; }
    setShareSending(true);
    try {
      await axios.post(`${API_URL}/auth/share`, {
        channel: shareChannel, recipient: shareRecipient,
        subject: shareSubject, messageText: shareMessageText,
        company_id: shareQuotation?.Company_id ?? null,
        quotation_id: shareQuotation?.id ?? null,
        pdf_url: shareQuotation?.letterhead_url || '',
        mediaUrl: shareQuotation?.letterhead_url || '',
      });
      alert('Quotation shared successfully!');
      setShareModalOpen(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to share quotation.');
    } finally { setShareSending(false); }
  };

  const handlePrint = () => {
    if (!previewQuotation) return;
    const comp = getCompany(previewQuotation.Company_id);
    const cust = getCustomer(previewQuotation.Customer_id);
    const lh   = resolveLetterhead(comp, previewQuotation);
    const html = buildPrintHtml(previewQuotation, comp, cust, lh, previewNumPages);

    const iframe = printIframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    // Wait for the letterhead <img> to fully load before triggering print
    const triggerPrint = () => {
      const imgs = doc.querySelectorAll('img');
      if (imgs.length === 0) {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        return;
      }
      let loaded = 0;
      const onLoad = () => {
        loaded++;
        if (loaded >= imgs.length) {
          setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          }, 200);
        }
      };
      imgs.forEach(img => {
        if ((img as HTMLImageElement).complete) { onLoad(); }
        else {
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onLoad); // proceed even on error
        }
      });
    };

    iframe.contentWindow?.addEventListener('load', triggerPrint);
    // Fallback: if load event already fired
    setTimeout(triggerPrint, 1500);
  };

      // Compute Dashboard Stats
      const totalValue = quotations.reduce((sum, q) => sum + Number(q.Total_Amount || 0), 0);
      const categoryStats = quotations.reduce((acc: Record<string, { count: number; sum: number }>, q: any) => {
        const cat = q.category || 'General';
        if (!acc[cat]) {
          acc[cat] = { count: 0, sum: 0 };
        }
        acc[cat].count += 1;
        acc[cat].sum += Number(q.Total_Amount || 0);
        return acc;
      }, {});

      return (
        <div className="p-6 max-w-7xl mx-auto text-left">
          {/* Hidden iframe for printing */}
          <iframe
            ref={printIframeRef}
            title="print-frame"
            style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, border: 'none' }}
            aria-hidden="true"
          />

          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-blue-900">Quotations &amp; Projects</h2>
              <p className="text-gray-500 text-xs mt-1">Create, manage, and share quotations and scope documents for clients.</p>
            </div>
            <button onClick={() => navigate('/quotation-creator')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase shadow transition">
              ➕ Create Quotation
            </button>
          </div>

          {/* Quotation Analytics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Card 1: Total Quotations */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-5 shadow-sm text-left">
              <span className="text-[10px] uppercase font-extrabold tracking-wider text-blue-900/60 block">Total Quotations</span>
              <h3 className="text-3xl font-black text-blue-950 mt-1">{quotations.length}</h3>
              <p className="text-[10px] text-blue-600/70 font-semibold mt-1">Active proposals & scopes</p>
            </div>

            {/* Card 2: Total Quotation Value */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-3xl p-5 shadow-sm text-left">
              <span className="text-[10px] uppercase font-extrabold tracking-wider text-indigo-900/60 block">Total Projected Value</span>
              <h3 className="text-2xl font-black text-indigo-950 mt-1.5">₹ {totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</h3>
              <p className="text-[10px] text-indigo-600/70 font-semibold mt-1">Sum of all amounts</p>
            </div>

            {/* Card 3: Categories Breakdown */}
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 rounded-3xl p-4 shadow-sm text-left md:col-span-2 overflow-y-auto max-h-[105px]">
              <span className="text-[9px] uppercase font-extrabold tracking-wider text-violet-900/60 block mb-2">Category Breakdown</span>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(categoryStats).map(([cat, stat]) => (
                  <div key={cat} className="flex justify-between items-center bg-white/70 border border-violet-100/50 rounded-xl px-2.5 py-1 text-[10px] shadow-sm">
                    <span className="font-bold text-slate-700 truncate max-w-[80px]" title={cat}>{cat}</span>
                    <span className="font-black text-violet-800">₹{stat.sum.toLocaleString('en-IN', { maximumFractionDigits: 0 })} <span className="text-[8px] text-slate-400 font-bold">({stat.count})</span></span>
                  </div>
                ))}
                {Object.keys(categoryStats).length === 0 && (
                  <span className="text-[10px] text-slate-400 italic">No categories tracked</span>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center text-gray-500 font-medium">Loading quotations...</div>
          ) : quotations.length === 0 ? (
            <div className="py-20 text-center text-gray-400">No quotations found. Click "Create Quotation" to get started.</div>
      ) : (
        <div className="bg-white shadow rounded-2xl overflow-hidden border border-blue-50">
          <table className="min-w-full divide-y divide-blue-50 text-sm">
            <thead className="bg-blue-50/50 text-left text-xs font-bold text-blue-900 uppercase">
              <tr>
                <th className="p-4">Quotation No</th>
                <th className="p-4">Version</th>
                <th className="p-4">Customer</th>
                <th className="p-4">Company</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-center">Status</th>
                <th className="p-4 text-right">Amount (₹)</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-50">
              {quotations.map(q => {
                const comp = getCompany(q.Company_id);
                const cust = getCustomer(q.Customer_id);
                return (
                  <tr key={q.id} className="hover:bg-blue-50/10">
                    <td className="p-4 font-bold text-blue-900">{q.QuotationNo}</td>
                    <td className="p-4 text-gray-550 font-semibold">{q.version || 'v1'}</td>
                    <td className="p-4 text-gray-700">{cust ? cust.company_name : `ID: ${q.Customer_id}`}</td>
                    <td className="p-4 text-gray-600">{comp ? comp.company_name : `ID: ${q.Company_id}`}</td>
                    <td className="p-4 text-gray-600">{new Date(q.Date).toLocaleDateString('en-GB')}</td>
                    <td className="p-4 text-center">
                      {q.status === 'Finalized' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-200">
                          Finalized
                        </span>
                      ) : q.status === 'Project Closed' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-xs font-bold border border-rose-200">
                          Closed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold border border-amber-200">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-right font-extrabold text-slate-800">₹{Number(q.Total_Amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button onClick={() => handleOpenPreview(q)} className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition">Print/View</button>
                      <button onClick={() => triggerShare(q, 'whatsapp')} className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition">Share</button>
                      <button onClick={() => navigate(`/quotation-creator?quotation_id=${q.id}`)} className="px-2.5 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-xs font-bold transition">Edit</button>
                      <button onClick={() => handleDelete(q.id)} className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition">Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Share Modal ── */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl border border-blue-50 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 px-6 py-4 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold">Share Quotation</h3>
              <button onClick={() => setShareModalOpen(false)} className="text-white hover:text-gray-200 font-bold text-xl">✕</button>
            </div>
            <div className="p-6 space-y-4 text-left">
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-2">Communication Channel</label>
                <div className="flex gap-2">
                  {[{ id: 'email', label: '✉️ Email' }, { id: 'sms', label: '💬 SMS' }, { id: 'whatsapp', label: '🟢 WhatsApp' }].map(ch => (
                    <button key={ch.id} type="button"
                      onClick={() => {
                        setShareChannel(ch.id as any);
                        if (shareQuotation) {
                          const cust = getCustomer(shareQuotation.Customer_id);
                          const comp = getCompany(shareQuotation.Company_id);
                          const custName = cust?.company_name || 'Customer';
                          const compName = comp?.company_name || 'Our Company';
                          const dateStr  = new Date(shareQuotation.Date).toLocaleDateString('en-GB');
                          const compPhone = comp?.phone || comp?.number || comp?.mobile || '';
                          const custPhone = cust?.number ? String(cust.number) : cust?.phone ? String(cust.phone) : '';
                          setShareRecipient(ch.id === 'email' ? (cust?.email || comp?.email || '') : (ch.id === 'whatsapp' ? (compPhone || custPhone) : (custPhone || compPhone)));
                          const msgs: Record<string, string> = {
                            sms:      `Dear ${custName}, quotation ${shareQuotation.QuotationNo} of ₹ ${shareQuotation.Total_Amount} is ready. - ${compName}`,
                            whatsapp: `Hello *${custName}*,\n\nHere is your quotation detail:\n*Quotation No:* ${shareQuotation.QuotationNo}\n*Date:* ${dateStr}\n*Total Amount:* ₹ ${shareQuotation.Total_Amount}\n\nRegards,\n*${compName}*`,
                            email:    `Hello ${custName},\n\nPlease find the quotation details below:\nQuotation No: ${shareQuotation.QuotationNo}\nDate: ${dateStr}\nTotal Amount: ₹ ${shareQuotation.Total_Amount}\n\nRegards,\n${compName}`,
                          };
                          setShareMessageText(msgs[ch.id]);
                        }
                      }}
                      className={`flex-1 py-2 px-3 text-xs font-bold border rounded-xl transition ${shareChannel === ch.id ? 'border-blue-600 text-blue-700 bg-blue-50/50 ring-2 ring-blue-100' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                    >{ch.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-semibold uppercase text-blue-900/80">
                    {shareChannel === 'email' ? 'Recipient Email Address' : 'Recipient Phone Number'}
                  </label>
                  {shareChannel !== 'email' && shareRecipient && (
                    <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-200">
                      Sending to: {shareRecipient}
                    </span>
                  )}
                </div>
                <input
                  type={shareChannel === 'email' ? 'email' : 'text'}
                  value={shareRecipient}
                  onChange={e => setShareRecipient(e.target.value)}
                  className="w-full border-2 border-blue-100 rounded-xl px-4 py-2.5 text-sm font-medium focus:border-blue-400 focus:outline-none bg-white text-gray-800 shadow-sm"
                  placeholder={shareChannel === 'email' ? 'e.g. client@email.com' : 'Enter mobile number (e.g. 919876543210)'}
                />
                {shareQuotation && shareChannel !== 'email' && (() => {
                  const cust = getCustomer(shareQuotation.Customer_id);
                  const comp = getCompany(shareQuotation.Company_id);
                  const compPhone = comp?.phone || comp?.number || comp?.mobile || '';
                  const custPhone = cust?.whatsapp_number ? String(cust.whatsapp_number) : (cust?.number ? String(cust.number) : (cust?.phone ? String(cust.phone) : ''));
                  return (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {compPhone ? (
                        <button
                          type="button"
                          onClick={() => setShareRecipient(compPhone)}
                          className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition flex items-center gap-1 ${shareRecipient === compPhone ? 'bg-blue-600 text-white border-blue-600 shadow-xs' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'}`}
                        >
                          🏢 Company: <span className="font-bold">{compPhone}</span>
                        </button>
                      ) : null}
                      {custPhone ? (
                        <button
                          type="button"
                          onClick={() => setShareRecipient(custPhone)}
                          className={`text-xs px-2.5 py-1 rounded-lg border font-medium transition flex items-center gap-1 ${shareRecipient === custPhone ? 'bg-blue-600 text-white border-blue-600 shadow-xs' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'}`}
                        >
                          👤 Customer: <span className="font-bold">{custPhone}</span>
                        </button>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
              {shareChannel === 'email' && (
                <div>
                  <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Email Subject</label>
                  <input type="text" value={shareSubject} onChange={e => setShareSubject(e.target.value)} className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none" />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase text-blue-900/80 mb-1">Message Body</label>
                <textarea value={shareMessageText} onChange={e => setShareMessageText(e.target.value)} rows={5} className="w-full border-2 border-blue-50 rounded-xl px-4 py-2 text-sm focus:border-blue-300 focus:outline-none font-mono" />
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button type="button" onClick={() => setShareModalOpen(false)} className="px-4 py-2 text-xs font-semibold text-gray-500 bg-white border rounded-xl hover:bg-gray-50 transition">Cancel</button>
              <button type="button" onClick={handleExecuteShare} disabled={shareSending} className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow-md transition disabled:opacity-50">{shareSending ? 'Sending...' : 'Send Now'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Print Preview Modal ── */}
      {previewQuotation && (() => {
        const comp = getCompany(previewQuotation.Company_id);
        const cust = getCustomer(previewQuotation.Customer_id);
        const lh   = resolveLetterhead(comp, previewQuotation);
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 md:p-8 flex flex-col items-center">
            <div className="w-full max-w-5xl flex flex-col mb-8">

              {/* Toolbar */}
              <div className="bg-gradient-to-r from-blue-900 to-indigo-800 px-6 py-4 text-white flex justify-between items-center rounded-t-3xl shadow-2xl flex-shrink-0">
                <div>
                  <h3 className="text-lg font-bold">Quotation Print Preview</h3>
                  <p className="text-xs text-blue-100 mt-0.5">
                    {lh
                      ? `✅ Letterhead loaded — ${previewNumPages} page${previewNumPages !== 1 ? 's' : ''} total.`
                      : '⚠️ No letterhead set — plain white pages will print.'}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button onClick={handlePrint} className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-xl shadow transition">
                    🖨 Print
                  </button>
                  <button onClick={handlePrint} className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold rounded-xl shadow transition">
                    💾 Save PDF
                  </button>
                  <button onClick={() => setPreviewQuotation(null)} className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition">
                    ✕ Close
                  </button>
                </div>
              </div>

              {/* Pages */}
              <div className="bg-slate-200 rounded-b-3xl p-8 flex flex-col items-center gap-6 overflow-x-auto">
                <PagedPreview
                  q={previewQuotation}
                  comp={comp}
                  cust={cust}
                  letterheadSrc={lh}
                  onNumPages={setPreviewNumPages}
                />

                {/* Print instructions */}
                <div className="text-center text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 shadow-sm" style={{ maxWidth: MM(PAGE_W_MM) }}>
                  <div className="font-bold text-amber-700 mb-2">📋 For best print results:</div>
                  <div className="space-y-1 text-left text-amber-800">
                    <div>1. Click <strong>Print / Save PDF</strong> above</div>
                    <div>2. In the print dialog → set <strong>Margins → None</strong></div>
                    <div>3. Uncheck <strong>Headers and footers</strong></div>
                    <div>4. Keep <strong>Background graphics ✓</strong> checked</div>
                    <div>5. The PDF will look <em>exactly</em> like this preview</div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default QuotationPage;
